import React from 'react';
import { useTableSort } from '../../../../_hooks';
import { HttpSortDirection } from '../../../../_http';
import CustomTable, { TTableColumn } from '../../../../_shared/custom/customTable/CustomTable';
import { translations } from '../../../../_translations';
import { TCashSheet, TDelivery, TFillCashSheetQueryFunction } from '../../../_models';
import { TDeliveryForm } from '../../../_forms/DeliveryForm';
import { addDefaultRows } from '../../../_utils';
import DeliveryForm from './DeliveryForm';

const DEFAULT_AMOUNT_OF_ROWS = 10;

const columns: TTableColumn[] = [
  {
    className: 'smallest-column centered-text',
    label: translations.getLabel('CASH_SHEETS.OVERVIEW.TABLE.ID'),
    name: 'orderNumber',
    sortable: true,
  },
  {
    className: 'large-column',
    label: translations.getLabel('CASH_SHEETS.OVERVIEW.TABLE.CLIENT_NAME'),
    name: 'clientName',
    sortable: true,
  },
  {
    className: 'small-column',
    label: translations.getLabel('CASH_SHEETS.OVERVIEW.TABLE.RECEIPT_CODE'),
    name: 'receiptCode',
    sortable: true,
  },
  {
    className: 'large-column',
    label: translations.getLabel('CASH_SHEETS.OVERVIEW.TABLE.DELIVERY_TYPE'),
    name: 'type',
    sortable: false,
  },
  { label: translations.getLabel('CASH_SHEETS.OVERVIEW.TABLE.CASH'), name: 'paidCash', sortable: false },
  { label: translations.getLabel('CASH_SHEETS.OVERVIEW.TABLE.BANCONTACT'), name: 'paidBancontact', sortable: false },
  { label: translations.getLabel('CASH_SHEETS.OVERVIEW.TABLE.CREDITCARD'), name: 'paidCreditcard', sortable: false },
  {
    label: translations.getLabel('CASH_SHEETS.OVERVIEW.TABLE.PORTABLE_BANCONTACT'),
    name: 'paidBancontactPortable',
    sortable: false,
  },
  {
    label: translations.getLabel('CASH_SHEETS.OVERVIEW.TABLE.PORTABLE_CREDITCARD'),
    name: 'paidCreditcardPortable',
    sortable: false,
  },
  { label: translations.getLabel('CASH_SHEETS.OVERVIEW.TABLE.COUPON'), name: 'paidCheck', sortable: false },
  {
    label: translations.getLabel('CASH_SHEETS.OVERVIEW.TABLE.BANK_TRANSFER'),
    name: 'paidBankTransfer',
    sortable: false,
  },
];

type TProps = {
  data?: TCashSheet;
  isLoading?: boolean;
  readOnly?: boolean;
  setQuery?: TFillCashSheetQueryFunction;
};

const DeliveryTable: React.FC<TProps> = ({ data, setQuery, readOnly, isLoading }) => {
  const { sortFunctions } = useTableSort((column: string, direction: HttpSortDirection) =>
    setQuery({ sortDeliveriesBy: column, sortDeliveriesDirection: direction }),
  );

  function renderRow(delivery: TDelivery, index: number): JSX.Element {
    const initialForm: TDeliveryForm = {
      clientName: delivery.clientName || '',
      orderNumber: delivery.orderNumber || 0,
      paidBancontact: delivery.paidBancontact || 0,
      paidBancontactPortable: delivery.paidBancontactPortable || 0,
      paidBankTransfer: delivery.paidBankTransfer || 0,
      paidCash: delivery.paidCash || 0,
      paidCheck: delivery.paidCheck || 0,
      paidCreditCard: delivery.paidCreditCard || 0,
      paidCreditCardPortable: delivery.paidCreditCardPortable || 0,
      receiptCode: delivery.receiptCode || '',
      type: delivery.type,
    };

    return (
      <DeliveryForm
        cashSheet={data}
        deliveryId={delivery.id}
        highlight={delivery.highlight}
        index={index}
        initialForm={initialForm}
        key={index}
      ></DeliveryForm>
    );
  }

  return (
    <CustomTable
      columns={columns}
      data={addDefaultRows<TDelivery, TDeliveryForm>(
        data?.deliveries,
        () => ({ orderNumber: (data?.deliveries || []).length }),
        DEFAULT_AMOUNT_OF_ROWS,
      )}
      isLoading={isLoading}
      readonly={readOnly}
      renderRow={renderRow}
      sortFunctions={sortFunctions}
    />
  );
};

export default DeliveryTable;
